import { browser } from '$app/environment'
import type { Achievement } from '$lib/achievements'
import { getAchievements } from '$lib/achievements'
import type { TypedSupabaseClient } from '$lib/supabase'
import type { PageDataSession } from '$lib/types'
import { writable } from 'svelte/store'
import { emptyReloadable, type Reloadable } from './reloadable'

export type NewAchievementsStore = Reloadable<Achievement[]> & {
  markAsSeen: (achievementCode: string | undefined) => Promise<void>
}

const noop = async () => {}

export const createNewAchievementsStore = (dependencies: {
  supabase: TypedSupabaseClient
  session: PageDataSession | null
}): NewAchievementsStore => {
  if (!browser) {
    // This store is only used to highlight new achievements to the user, so it
    // only makes sense to use it in the browser.
    return { ...emptyReloadable, markAsSeen: noop }
  }

  const reload = async (): Promise<Achievement[]> => {
    const $session = dependencies.session
    if (!$session) {
      _newAchievements.set([])
      return []
    }

    const achievements = await getAchievements(
      dependencies.supabase,
      $session.profile,
      {
        onlyAchieved: true,
        onlyNew: true,
      },
    )

    _newAchievements.set(achievements)
    return achievements
  }

  const _newAchievements = writable<Achievement[]>([], () => {
    reload()
  })

  return {
    subscribe: _newAchievements.subscribe,
    reload,
    markAsSeen: async (achievementId: string | undefined) => {
      if (!dependencies.session || !achievementId) return

      await dependencies.supabase.rpc('mark_achievement_seen', {
        _id: achievementId,
      })
    },
  }
}
